import { useState } from 'react'

import { AlertBox } from '@/components/common/AlertBox'
import { ButtonDefault, ButtonRoot } from '@/components/common/Button'
import { useFloating } from '@/components/common/Floating'
import { LoadingMask } from '@/components/common/LoadingMask'
import { type CardIdType } from '@/defs/card'
import { useReduxDispatch } from '@/store/hooks'
import { userThunks } from '@/store/user'
import { cn, parseError } from '@/utils/commonUtil'

import { CardFaces } from '../CardFaces/CardFaces'
import { useCardArtChooser } from '../hooks/useCardArtChooser'

import styles from './CardArtModal.module.scss'

type Props = {
  cardId: CardIdType
}

export const CardArtModal = ({ cardId }: Props) => {
  const { close } = useFloating()
  const dispatch = useReduxDispatch()
  const { selected, uniqueArtwork } = useCardArtChooser(cardId)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const onClickSelect = (scryfallId: string) => {
    setError('')
    setLoading(true)
    void (async () => {
      try {
        await dispatch(userThunks.setAlternateArt({ cardId, scryfallId })).unwrap()
        close()
      } catch (e) {
        const message = parseError(e, 'Update failed, please try again')
        setError(message)
      } finally {
        setLoading(false)
      }
    })()
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>Choose Preferred Artwork</h2>
      {error && (
        <div className={styles.alert}>
          <AlertBox>
            <p>
              <strong>Error:</strong> {error}
            </p>
          </AlertBox>
        </div>
      )}
      <ul className={styles.optionList}>
        {uniqueArtwork?.map(
          ({ artist, image, imageBack, scryfallId, setName }, i) =>
            scryfallId &&
            image && (
              <li key={i}>
                <ButtonRoot
                  className={cn(styles.option, { isSelected: selected === scryfallId, styles })}
                  disabled={loading}
                  onClick={() => onClickSelect(scryfallId)}
                >
                  <div className={styles.face}>
                    <CardFaces
                      back={imageBack ? { image: imageBack } : undefined}
                      front={{ image }}
                      variant={imageBack ? 'dfc' : 'standard'}
                    />
                  </div>
                  <p className={styles.setName}>{setName}</p>
                  <p className={styles.artist}>{artist}</p>
                </ButtonRoot>
              </li>
            ),
        )}
      </ul>
      {selected && !loading && (
        <div className={styles.revert}>
          <ButtonDefault onClick={() => onClickSelect('')}>Revert to Default</ButtonDefault>
        </div>
      )}
      <LoadingMask bgNone loading={loading} showSpinner zIndex='var(--z-mask)' />
    </div>
  )
}
